import React, {useContext, useEffect, useState} from 'react';
import {useQuery} from "@tanstack/react-query";
import vmsService from "../../../../../../services/web/vms-service.js";
import {Card} from "../../../../../../components/card/card.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../../../utils/aggrid-utils.js";
import {FaGear} from "react-icons/fa6";
import {FaExternalLinkSquareAlt} from "react-icons/fa";
import Button from "../../../../../../components/button/button.jsx"
import {Modal} from "../../../../../../components/modal/modal.jsx";
import InputRadio from "../../../../../../components/input/input-radio.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import {AgentContext} from "../../context/agent-context.jsx";
import {toast} from "react-toastify";
import projectUtils from "../../../../../../utils/project-utils.js";
import AlertMessages from "../../../../../../components/messages/alert-message.js";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

const Vms = ({user, setIsLoading}) => {
    const {userInfo, id, userRefetch} = useContext(AgentContext);
    const [dataGrid, setDataGrid] = useState([])
    const [vmsGroups, setVmsGroups] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [accessType, setAccessType] = useState("");
    const [dataOpened, setDataOpened] = useState()

    const onChangeRadio = (value) => {
        setAccessType(value);
        if (value !== "onlySentry") setVmsGroups([]);
    }

    const {data: vmsConfigurations, refetch: vmsConfigurationRefetch} = useQuery({
        queryFn: () => vmsService.getAll(),
        queryKey: ["vmsConfigurations"]
    });

    useEffect(() => {
        if (!dataOpened) return
        if (dataOpened.vmsUser && dataOpened.cellType === "onlySentry") {
            setVmsGroups(
                dataOpened.vmsUser?.userAccessGroups.map(({accessGroup: group}) => (
                    {
                        vmsId: group.vmsConfigurationId,
                        value: group.id,
                        label: group.groupName
                    }
                ))
            )
        } else setVmsGroups([]);

        setAccessType(dataOpened.cellType);
    }, [showModal, dataOpened]);
    useEffect(() => {
        if (!vmsConfigurations) return;

        setDataGrid(vmsConfigurations.map(item => {
            const find = user?.vmsUserConfiguration?.find(i => i.vmsConfiguration.id === item.id);
            item.name = item.vmsCorporationName ?? item.vmsUrl

            if(find) {
                item.vinculatedAt = find ? projectUtils.formatDate(find.vinculatedAt) : ""
                item.userBind = !!find;

                const groups = find?.userAccessGroups.map(({accessGroup}) => accessGroup.groupName).join(", ")
                item.type = find?.vmsApiKeyBound ? "Sentry + VMS" : find?.userAccessGroups.length ? `Sentry (${groups})` : "Sem acesso";
                item.cellType = find?.vmsApiKeyBound ? "sentryAndVms" : find?.userAccessGroups.length ? "onlySentry" : "withoutAccess";
                item.vmsUser = find;
            }

            if (!item.type) item.type = "Não vinculado"

            return item;
        }))
    }, [vmsConfigurations, user?.vmsUserConfiguration]);

    const openVms = (vmsId) => {
        const vmsTab = window.open("", "_blank");
        vmsTab.opener = null;
        axios.get(`${API_URL}/vms/user-open/${id}/${vmsId}`)
            .then(({data}) => vmsTab.location.href = data)
            .catch(() => {
                vmsTab.close();
                projectUtils.swalErrorMessage("", "Não foi possível abrir o VMS.", "Ok");
            })
    }

    const [columnDefs] = useState([
        {headerName: "Nome", field: "name", flex: 1},
        {headerName: "Tipo do vínculo", field: "type", flex: 1},
        {headerName: "D/H do vínculo", field: "vinculatedAt", flex: 1},
        {
            headerName: "Ações",
            field: "",
            width: 100,
            cellRenderer: ({data}) => {
                const config = () => {
                    setDataOpened(data)
                    setShowModal(true)
                }

                return <div className={"flex gap-2"}>
                    <Button className={"!w-[30px] !h-[25px] !p-0 mt-px"} onClick={config} title={"Configurar vínculo com " + data.name}>
                        <FaGear />
                    </Button>
                    <Button className={"!w-[30px] !h-[25px] !p-0 mt-px"} disabled={data.cellType !== "sentryAndVms"} onClick={() => openVms(data.id)} title={"Abrir cadastro no " + data.name}>
                        <FaExternalLinkSquareAlt />
                    </Button>
                </div>
            },
        }
    ])

    const apply = () => {
        switch (accessType) {
            case "withoutAccess":
                unbind()
                break
            case "sentryAndVms":
                bind()
                break
            case "onlySentry":
                bindByGroups()
                break
        }
    }

    const bindByGroups = () => {
        if (!vmsGroups.length) {
            projectUtils.swalErrorMessage("", "O campo \"Grupo de acesso\" é obrigatório.")
            return
        }

        const postData = {
            vmsConfigurationId: dataOpened?.id,
            accessGroupIds: vmsGroups.map(item => item.value),
            userId: id
        }

        setIsLoading(true);
        vmsService.groupsBind(postData)
            .then(() => {
                toast.success("Vinculado com sucesso");
                userRefetch()
                setShowModal(false)
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível vincular.';
                if (error.response.status !== 500) {
                    message = AlertMessages[error.response.data.messageKey] ?? "Não foi possível salvar.";
                }
                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const bind = () => {
        setIsLoading(true);
        vmsService.userBind(id, dataOpened?.id)
            .then(() => {
                toast.success("Vinculado com sucesso");
                userRefetch()
                setShowModal(false)
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível vincular.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }
                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    const unbind = async () => {
        setIsLoading(true);
        await vmsService.userUnbind(id, dataOpened?.id)
            .then(async () => {
                await new Promise(resolve => setTimeout(resolve, 2000));
                toast.success("Desvinculado com sucesso");
                userRefetch()
                vmsConfigurationRefetch()
                setShowModal(false)
            })
            .catch(error => {
                console.error(error);
                let message = 'Não foi possível vincular.';
                if (error.response.status !== 500) {
                    message = error.response.data.message;
                }
                projectUtils.swalErrorMessage('', message, 'Ok');
            })
            .finally(() => setIsLoading(false));
    }

    return (
        <>
            <Card>
                <Card.Header>
                    <Card.Title>Vigia Mais</Card.Title>
                </Card.Header>
                <Card.Body>
                    <div className={"vms-grid"}>
                        <AgGridReact
                            rowData={dataGrid}
                            columnDefs={columnDefs}
                            animateRows={false}
                            suppressCellFocus={true}
                            gridOptions={{localeText: aggridUtils.localization()}}
                            theme={aggridUtils.generalTheme()}
                            domLayout='autoHeight'
                        />
                    </div>
                </Card.Body>
            </Card>

            <Modal isCloseClick={false} overflow={true} open={showModal} setOpen={setShowModal} centered size="xl" backdrop="static" keyboard={false} title={`Configurar vínculo com ${(dataOpened?.name || (dataOpened?.vmsCorporationName || dataOpened?.vmsUrl))}`} className={"w-4/12"}>
                <Modal.Body className="p-2">
                    <div className={"mb-2"}>
                        <InputRadio
                            id={"withoutAccess"}
                            name={"withoutAccess"}
                            classNameLabel={"!text-[16px]"}
                            label={"Sem acesso"}
                            checked={accessType === "withoutAccess"}
                            onChange={() => onChangeRadio("withoutAccess")}
                        />
                    </div>
                    <div className={"mb-2"}>
                        <InputRadio
                            id={"sentryAndVms"}
                            name={"vms-access"}
                            classNameLabel={"!text-[16px]"}
                            label={"Sentry + " + (dataOpened?.name || (dataOpened?.vmsCorporationName || dataOpened?.vmsUrl))}
                            checked={accessType === "sentryAndVms"}
                            onChange={() => onChangeRadio("sentryAndVms")}
                        />
                    </div>

                    <div className={"mb-2"}>
                        <InputRadio
                            id={"onlySentry"}
                            name={"onlySentry"}
                            classNameLabel={"!text-[16px]"}
                            label={"Apenas pelo Sentry"}
                            checked={accessType === "onlySentry"}
                            onChange={() => onChangeRadio("onlySentry")}
                        />
                    </div>

                    <SelectComponent
                        isMulti
                        instanceId={"vmsAccessGroup"}
                        value={vmsGroups}
                        options={userInfo?.vmsAccessGroups?.filter((item) => item.vmsId === dataOpened?.id) ?? []}
                        label={"Grupo de acesso *"}
                        onChange={(selected) => setVmsGroups(selected)}
                        isDisabled={accessType !== "onlySentry"}
                    />
                </Modal.Body>

                <Modal.Footer className={"p-2"}>
                    <Button onClick={apply}>Salvar</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default Vms;